// engine-exception: auth/onboarding flow, not a resource screen — stays hand-written (blueprint §1: web/src/pages is legacy)
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Nova, NovaMark, Icon, type IconName } from '../engine/design';

// Public landing page — the marketing front door AND a live taste of the product. It reuses
// the app's own primitives (.stat, .card, .table-card) inside an interactive deck, so the
// pitch and the platform are visibly one design system. Shown to logged-out visitors at "/".
//
// The page tells a story: hook the time-starved MSP (run your MSP, not your PSA) → why another PSA
// exists (legacy = stable depth but walled-garden; modern = open APIs but forgot UX; NovaPSA = both,
// done right) → the red-panda way (real biology tied to real mechanisms: inheritance/templates to
// child records, capture-once flow, bulk actions + policy workflows, a modernized-legacy UI) → the
// modules Nova is building → an interface preview → status: under way, opening soon.
//
// HONESTY: the platform is early (days old) and in active development. The copy describes what
// NovaPSA IS and DOES by design; it must NOT claim the modules are shipped/built, and must NOT claim
// they aren't — the status band says "under way, opening soon". No superlative/fact-checkable animal
// claims, no "single pane of glass"/"everything in one place", no "free"/"get started".
//
// Branding: NovaPSA, the red-panda PSA. Dark→rust→cream hero band (Nova's own colours), real CC0
// red-panda photography (web/public/panda-*.jpg, public domain, no attribution), and the Nova mark.

const REDUCE = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

const SLIDES = [
  { id: 'dash', label: 'Dashboard' },
  { id: 'desk', label: 'Service desk' },
  { id: 'projects', label: 'Projects' },
  { id: 'quoting', label: 'Quoting' },
  { id: 'ledger', label: 'Ledger' },
] as const;
type SlideId = (typeof SLIDES)[number]['id'];

// The full PSA surface — what NovaPSA is.
const MODULES: { icon: IconName; name: string; blurb: string }[] = [
  { icon: 'user-plus', name: 'CRM', blurb: 'Companies, contacts, sites and the sales pipeline — one source of truth.' },
  { icon: 'life-buoy', name: 'Service desk', blurb: 'ITIL tickets, SLA clocks, CSAT and a visual workflow designer.' },
  { icon: 'gantt-chart', name: 'Projects', blurb: 'Phases, dependencies, budgets and table/board/timeline/workload views.' },
  { icon: 'file-check', name: 'Quoting', blurb: 'Catalog quotes with e-sign that convert to invoice, contract or project.' },
  { icon: 'message-square', name: 'Email', blurb: 'Mailboxes, inbound email-to-ticket and threaded, templated replies.' },
  { icon: 'app-window', name: 'Customer portal', blurb: 'Self-service tickets, quotes and invoices on a locked-down role.' },
  { icon: 'calendar-days', name: 'Time & rates', blurb: 'One time engine for tickets and projects; cost and charge rates resolved for the work date.' },
  { icon: 'book-open', name: 'Contracts & billing', blurb: 'Recurring contracts and invoices on a hash-chained, append-only ledger.' },
];

// The red-panda story — real traits (efficiency, the evolved "false thumb", nimble climbing, a
// lineage all its own) tied to what the product does. This is the narrative spine of the page.
const TRAITS: { icon: IconName; title: string; body: string; nature: string }[] = [
  {
    icon: 'git-branch',
    title: 'Set it once, inherit everywhere',
    body: 'Define a rate, an SLA or a config on a parent and let it cascade to every client, site and asset beneath it. Change the parent and the children follow — instead of re-entering the same setting across hundreds of records.',
    nature: 'Red pandas run on a low-energy metabolism — the art of getting the most from the least.',
  },
  {
    icon: 'file-check',
    title: 'Capture it once, carry it through',
    body: 'A quote becomes a ticket becomes tracked time becomes an invoice on the same underlying records. What you enter at one step is already there at the next, rather than being re-keyed.',
    nature: 'A red panda’s “false thumb” is an evolved wrist bone — the exact tool for the job, grown right in.',
  },
  {
    icon: 'check-square',
    title: 'Handle a hundred at a time',
    body: 'Select records and act in bulk — reassign, retag, re-rate, resolve — and let workflows route, approve and escalate by your rules, so policy is followed without anyone having to remember it.',
    nature: 'Nimble in the canopy, red pandas use a long ringed tail to move quickly and keep their balance.',
  },
  {
    icon: 'layout-dashboard',
    title: 'Familiar, finally modern',
    body: 'The density MSP pros rely on — dense tables, saved views, keyboard-driven navigation — in an interface built this decade, not one carried over from the last.',
    nature: 'The only living species in its family: an old lineage on its own branch, brought up to date.',
  },
];

// Status / priority / SLA chips reuse the app's pill tones.
type Tone = 'pos' | 'neg' | 'warn' | 'info' | 'muted';
function chip(text: string, tone: Tone) {
  const m: Record<Tone, string> = { pos: 'lp-p-paid', neg: 'lp-p-over', warn: 'lp-p-warn', info: 'lp-p-issued', muted: 'lp-p-draft' };
  return <span className={`lp-pill ${m[tone]}`}>{text}</span>;
}

function useCountUp(target: number, run: boolean): number {
  const [v, setV] = useState(REDUCE ? target : 0);
  useEffect(() => {
    if (!run || REDUCE) { setV(target); return; }
    let raf = 0;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / 1200);
      setV(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, target]);
  return v;
}

const money = (n: number) => '$' + Math.round(n).toLocaleString('en-US');

// A generic trend line (ticket volume on the dashboard).
function TrendChart() {
  const ref = useRef<SVGPathElement>(null);
  useEffect(() => {
    if (REDUCE || !ref.current) return;
    const len = ref.current.getTotalLength();
    ref.current.style.strokeDasharray = String(len);
    ref.current.style.strokeDashoffset = String(len);
    ref.current.getBoundingClientRect();
    ref.current.style.transition = 'stroke-dashoffset 1.6s cubic-bezier(.22,1,.36,1) .2s';
    ref.current.style.strokeDashoffset = '0';
  }, []);
  const data = [61, 58, 64, 55, 52, 49, 53, 48, 46, 44, 43, 41];
  const W = 560, H = 150, pad = 8;
  const max = Math.max(...data), min = Math.min(...data) - 4;
  const x = (i: number) => pad + (i * (W - 2 * pad)) / (data.length - 1);
  const y = (v: number) => H - pad - ((v - min) / (max - min)) * (H - 2 * pad);
  const d = 'M' + data.map((v, i) => `${x(i)} ${y(v)}`).join(' L ');
  return (
    <svg className="lp-chart" viewBox="0 0 560 150" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="lpg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--nova-rust)" stopOpacity=".42" />
          <stop offset="1" stopColor="var(--nova-rust)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L ${x(data.length - 1)} ${H} L ${x(0)} ${H} Z`} fill="url(#lpg)" />
      <path ref={ref} d={d} fill="none" stroke="var(--nova-rust)" strokeWidth="2.5" strokeLinecap="round"
        style={{ filter: 'drop-shadow(0 4px 10px rgba(192,90,47,.5))' }} />
      <circle cx={x(data.length - 1)} cy={y(data[data.length - 1]!)} r="4.5" fill="var(--nova-rust)" />
    </svg>
  );
}

// The technician's cross-module home: tickets, SLA, projects — plus the books underneath.
function DashSlide() {
  const tickets = useCountUp(47, true);
  const projects = useCountUp(12, true);
  const mrr = useCountUp(248300, true);
  return (
    <>
      <div className="lp-slide-head"><h3>Today</h3><span>Across every module</span><span className="lp-tag">live</span></div>
      <div className="stat-grid">
        <div className="stat accent"><div className="label">Open tickets</div><div className="value mono">{Math.round(tickets)}</div><div className="sub" style={{ color: 'var(--c-neg)' }}>8 breaching SLA</div></div>
        <div className="stat"><div className="label">SLA on-time</div><div className="value mono">96.4%</div><div className="sub" style={{ color: 'var(--c-pos)' }}>▲ 1.8 pts this month</div></div>
        <div className="stat"><div className="label">Active projects</div><div className="value mono">{Math.round(projects)}</div><div className="sub">3 due this week</div></div>
        <div className="stat"><div className="label">Recurring revenue</div><div className="value mono">{money(mrr)}</div><div className="sub" style={{ color: 'var(--c-pos)' }}>● chain-verified books</div></div>
      </div>
      <div className="lp-row2">
        <div className="card card-pad"><div className="card-title">Ticket volume</div><p className="muted" style={{ margin: '2px 0 10px', fontSize: 'var(--fs-12)' }}>Trailing 12 weeks — trending down</p><TrendChart /></div>
        <div className="card card-pad"><div className="card-title">Hours logged</div><p className="muted" style={{ margin: '2px 0 10px', fontSize: 'var(--fs-12)' }}>This week, by day</p>
          <div className="lp-bars">{[52, 60, 48, 66, 72, 80, 40].map((v, i) => (
            <i key={i} style={{ height: `${v}%`, animationDelay: `${REDUCE ? 0 : i * 70}ms` }} />
          ))}</div>
        </div>
      </div>
    </>
  );
}

function ServiceDeskSlide() {
  const rows: [string, string, string, [string, Tone], [string, Tone], [string, Tone]][] = [
    ['INC-4821', 'Exchange mailbox not syncing', 'Acme Corp', ['P1', 'neg'], ['12m left', 'warn'], ['In progress', 'info']],
    ['INC-4809', 'VPN drops every ~30 minutes', 'Northwind', ['P2', 'warn'], ['On time', 'pos'], ['In progress', 'info']],
    ['REQ-4816', 'New starter — laptop + M365', 'Globex', ['P3', 'info'], ['2h left', 'pos'], ['Awaiting', 'warn']],
    ['CHG-4802', 'Firewall firmware upgrade', 'Umbrella IT', ['P3', 'info'], ['Scheduled', 'muted'], ['Approved', 'pos']],
    ['INC-4795', 'Printer offline — 3rd floor', 'Initech', ['P4', 'muted'], ['Breached', 'neg'], ['Open', 'warn']],
  ];
  return (
    <>
      <div className="lp-slide-head"><h3>Service desk</h3><span>My team’s queue</span><span className="lp-tag">8 breaching SLA</span></div>
      <div className="table-card"><table className="data">
        <thead><tr><th>Ref</th><th>Subject</th><th className="lp-hide-sm">Client</th><th>Priority</th><th className="lp-hide-sm">SLA</th><th style={{ textAlign: 'right' }}>Status</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r[0]}>
            <td className="mono" style={{ color: 'var(--c-text-2)' }}>{r[0]}</td>
            <td style={{ fontWeight: 600 }}>{r[1]}</td>
            <td className="lp-hide-sm">{r[2]}</td>
            <td>{chip(r[3][0], r[3][1])}</td>
            <td className="lp-hide-sm">{chip(r[4][0], r[4][1])}</td>
            <td style={{ textAlign: 'right' }}>{chip(r[5][0], r[5][1])}</td>
          </tr>
        ))}</tbody></table></div>
      <div className="lp-chain">Every action carries an outcome that drives status, SLA holds and emails — automated by rules or the workflow designer.</div>
    </>
  );
}

function ProjectsSlide() {
  const tasks: [string, string, [string, Tone], string][] = [
    ['Discovery & audit', 'DO', ['Done', 'pos'], 'Jun 24'],
    ['Tenant provisioning', 'SR', ['Done', 'pos'], 'Jun 30'],
    ['Mailbox cutover — batch 1', 'AK', ['In progress', 'info'], 'Jul 8'],
    ['Endpoint enrollment', 'MG', ['Blocked', 'neg'], 'Jul 10'],
    ['User training', '—', ['To do', 'muted'], 'Jul 15'],
  ];
  return (
    <>
      <div className="lp-slide-head"><h3>Microsoft 365 migration</h3><span>Acme Corp · 5 phases</span><span className="lp-tag">68% complete</span></div>
      <div className="lp-bar" style={{ marginBottom: 14 }}><i style={{ ['--w' as string]: '0.68' }} /></div>
      <div className="table-card"><table className="data">
        <thead><tr><th>Task</th><th>Owner</th><th>Status</th><th className="lp-hide-sm" style={{ textAlign: 'right' }}>Due</th></tr></thead>
        <tbody>{tasks.map((t) => (
          <tr key={t[0]}>
            <td style={{ fontWeight: 600 }}>{t[0]}</td>
            <td><span className="lp-ava">{t[1]}</span></td>
            <td>{chip(t[2][0], t[2][1])}</td>
            <td className="mono lp-hide-sm" style={{ textAlign: 'right' }}>{t[3]}</td>
          </tr>
        ))}</tbody></table></div>
      <div className="lp-chain">Time logged 89h of a 120h budget — burn tracked from every technician’s time entry, the same entries that bill.</div>
    </>
  );
}

function QuotingSlide() {
  const rows: [string, string, string, string][] = [
    ['Managed services — 20 seats', '20', '$95.00 / mo', '$1,900.00 / mo'],
    ['Microsoft 365 Business Premium', '20', '$22.00 / mo', '$440.00 / mo'],
    ['Backup & DR — per server', '4', '$40.00 / mo', '$160.00 / mo'],
    ['Onboarding & migration', '1', '$3,500.00', '$3,500.00'],
  ];
  return (
    <>
      <div className="lp-slide-head"><h3>Quote QUO-1043</h3><span>Northwind Traders</span><span className="lp-tag">Accepted</span></div>
      <div className="table-card"><table className="data">
        <thead><tr><th>Item</th><th style={{ textAlign: 'right' }}>Qty</th><th className="lp-hide-sm" style={{ textAlign: 'right' }}>Unit</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r[0]}><td style={{ fontWeight: 600 }}>{r[0]}</td><td className="mono" style={{ textAlign: 'right' }}>{r[1]}</td><td className="mono lp-hide-sm" style={{ textAlign: 'right' }}>{r[2]}</td><td className="mono" style={{ textAlign: 'right' }}>{r[3]}</td></tr>
        ))}</tbody></table></div>
      <div className="lp-quote-foot">
        <span>Monthly <b className="mono">$2,500.00</b> &nbsp;·&nbsp; One-time <b className="mono">$3,500.00</b></span>
        <span className="lp-chain" style={{ margin: 0 }}>✓ Signed by J. Marsh — converts to an invoice, contract &amp; project in one click.</span>
      </div>
    </>
  );
}

function LedgerSlide() {
  const rows = [
    ['#128940', 'Invoice INV-2041 issued — Acme', '$12,400.00', '—', '$86,420.00'],
    ['#128939', 'Payment received — Northwind', '—', '$4,250.00', '$74,020.00'],
    ['#128938', 'Credit note CN-118 — Globex', '—', '$1,150.00', '$78,270.00'],
    ['#128937', 'Invoice INV-2039 issued — Globex', '$9,800.00', '—', '$79,420.00'],
  ];
  return (
    <>
      <div className="lp-slide-head"><h3>Ledger — the books underneath</h3><span>Append-only</span><span className="lp-tag">immutable</span></div>
      <div className="table-card"><table className="data"><thead><tr><th>Entry</th><th>Memo</th><th style={{ textAlign: 'right' }}>Debit</th><th style={{ textAlign: 'right' }}>Credit</th><th className="lp-hide-sm" style={{ textAlign: 'right' }}>Balance</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r[0]}><td className="mono" style={{ color: 'var(--c-text-2)' }}>{r[0]}</td><td>{r[1]}</td><td className="mono" style={{ textAlign: 'right' }}>{r[2]}</td><td className="mono" style={{ textAlign: 'right' }}>{r[3]}</td><td className="mono lp-hide-sm" style={{ textAlign: 'right' }}>{r[4]}</td></tr>
        ))}</tbody></table></div>
      <div className="lp-chain">✓ Chain verified · 128,940 entries · no gaps &nbsp;·&nbsp; every ticket, project and quote settles here — corrections are reversals, never edits.</div>
    </>
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState<SlideId>('dash');

  const go = (id: SlideId) => setActive(id);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const i = SLIDES.findIndex((s) => s.id === active);
      if (e.key === 'ArrowRight') setActive(SLIDES[(i + 1) % SLIDES.length]!.id);
      if (e.key === 'ArrowLeft') setActive(SLIDES[(i - 1 + SLIDES.length) % SLIDES.length]!.id);
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [active]);

  return (
    <div className="lp">
      <header className="lp-nav">
        <div className="lp-brand"><NovaMark size={28} animated /><span className="lp-word">Nova<span className="lp-brand-psa">PSA</span></span></div>
        <div className="lp-nav-right">
          <button className="btn ghost" onClick={() => navigate('/login')}>Sign in</button>
          <button className="btn primary" onClick={() => navigate('/login')}>Create account</button>
        </div>
      </header>

      {/* Coming-soon banner — the platform is built; public launch is what's coming. */}
      <div className="lp-banner" role="status">
        <span className="lp-banner-badge">Coming soon</span>
        <span>NovaPSA is opening up soon. Already have an account? <button className="lp-banner-link" onClick={() => navigate('/login')}>Sign in →</button></span>
      </div>

      {/* Hero band — Nova's own dark → rust → cream, full-bleed, with a real red panda. */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-copy">
            <span className="lp-eyebrow">Meet Nova — the red-panda PSA</span>
            <h1 className="lp-title">Run your MSP, <em>not your PSA.</em></h1>
            <p className="lp-lede">
              You didn’t start an MSP to run a PSA — but the tools that manage your clients, tickets,
              projects and billing pile on the admin: the same settings copied across every account,
              statuses chased by hand, policy enforced from memory. Nova is out to cut that overhead —
              set things once, act in bulk, and let workflows carry your policy — so the day goes back to the work.
            </p>
            <div className="lp-cta">
              <button className="btn primary lg" onClick={() => navigate('/login')}>Sign in</button>
              <button className="btn line lg" onClick={() => go('desk')}>Take a look ↓</button>
            </div>
            <div className="lp-hero-meta">
              <span>✓ Cascade settings across clients &amp; sites</span><span>✓ Bulk edits &amp; actions</span><span>✓ Workflows that enforce policy</span>
            </div>
          </div>
          <figure className="lp-hero-art">
            <img src="/panda-hero.jpg" alt="A red panda perched on a branch — Nova, the NovaPSA mascot"
              width={1280} height={848} loading="eager" />
            <figcaption><Nova variant="mark" size={20} /> Meet Nova</figcaption>
          </figure>
        </div>
      </section>

      {/* Why another PSA — the vision: legacy depth + modern integration + the UX neither nails. */}
      <section className="lp-why-wrap">
        <div className="lp-section-head">
          <span className="lp-kicker">Why another PSA?</span>
          <h2>The best of both, without the compromises</h2>
          <p>MSP platforms make you choose between depth and delight. Nova refuses to.</p>
        </div>
        <div className="lp-compare">
          <div className="lp-cmp">
            <div className="lp-cmp-h">Legacy platforms</div>
            <p><b>Great at</b> stable, dependable workload management and finance you can trust.</p>
            <p><b>But</b> they’re walled gardens — painful to integrate with, and they constrain you to their ecosystem.</p>
          </div>
          <div className="lp-cmp">
            <div className="lp-cmp-h">Modern platforms</div>
            <p><b>Great at</b> integrations and open, API-first ecosystems.</p>
            <p><b>But</b> somewhere along the way they forgot the science of good UX — and stopped respecting your attention or your clicks.</p>
          </div>
          <div className="lp-cmp lp-cmp-nova">
            <div className="lp-cmp-h"><NovaMark size={18} /> NovaPSA</div>
            <p>Deep financial and operational expertise, a modern UI with legacy-grade UX — less admin, efficient navigation, workflows that follow your policy.</p>
            <p>API-first at the core, and built for what’s next.</p>
          </div>
        </div>
      </section>

      {/* The red-panda way — efficiency traits tied to real red-panda biology. */}
      <section className="lp-traits-wrap">
        <div className="lp-section-head">
          <span className="lp-kicker">The red-panda way</span>
          <h2>Efficiency, by nature</h2>
          <p>Red pandas thrive by doing more with less. Nova brings the same instinct to your workday.</p>
        </div>
        <div className="lp-mod-grid">
          {TRAITS.map((t) => (
            <div className="lp-mod lp-trait" key={t.title}>
              <span className="lp-mod-ic"><Icon name={t.icon} size={20} /></span>
              <div className="lp-mod-name">{t.title}</div>
              <p>{t.body}</p>
              <p className="lp-trait-nat"><NovaMark size={14} /> <span>{t.nature}</span></p>
            </div>
          ))}
        </div>
      </section>

      {/* The PSA surface — the modules Nova is building on one shared engine. */}
      <section className="lp-modules">
        <div className="lp-section-head">
          <span className="lp-kicker">The platform</span>
          <h2>Everything an MSP runs on</h2>
          <p>Modules on a shared engine, so screens, permissions and reporting stay consistent across all of them.</p>
        </div>
        <div className="lp-mod-grid">
          {MODULES.map((m) => (
            <div className="lp-mod" key={m.name}>
              <span className="lp-mod-ic"><Icon name={m.icon} size={20} /></span>
              <div className="lp-mod-name">{m.name}</div>
              <p>{m.blurb}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Interface preview — built from the app's own design system. */}
      <section className="lp-deck-wrap">
        <div className="lp-section-head">
          <span className="lp-kicker">The interface</span>
          <h2>Designed to get out of your way</h2>
          <p>A preview built from Nova’s own design system — flip through the service desk, projects, quoting and the ledger it all settles on.</p>
        </div>
        <section className="lp-deck" aria-label="Product tour">
          <div className="lp-segs" role="tablist">
            {SLIDES.map((s, i) => (
              <button key={s.id} role="tab" aria-selected={active === s.id} className="lp-seg" onClick={() => go(s.id)}>
                <small>{String(i + 1).padStart(2, '0')}</small>{s.label}
              </button>
            ))}
          </div>
          <div className="lp-stage">
            <div className="lp-slide" key={active}>
              {active === 'dash' && <DashSlide />}
              {active === 'desk' && <ServiceDeskSlide />}
              {active === 'projects' && <ProjectsSlide />}
              {active === 'quoting' && <QuotingSlide />}
              {active === 'ledger' && <LedgerSlide />}
            </div>
          </div>
        </section>
      </section>

      {/* Launch band with a second red panda. */}
      <section className="lp-soon">
        <figure className="lp-soon-art">
          <img src="/panda-play.jpg" alt="A red panda close-up" width={1280} height={853} loading="lazy" />
        </figure>
        <div className="lp-soon-copy">
          <span className="lp-kicker">Where things stand</span>
          <h2>Under way — and opening up soon</h2>
          <p>
            Nova is building her PSA — CRM, a service desk with a workflow designer, projects, quoting,
            an email engine and the customer portal, all on one shared engine over an independent
            financial core. It’s early days and moving fast; we’re opening access as it comes together.
          </p>
          <div className="lp-cta">
            <button className="btn line lg" onClick={() => navigate('/login')}>Sign in to your workspace</button>
          </div>
        </div>
      </section>

      <footer className="lp-foot">
        <span className="lp-foot-brand"><NovaMark size={20} /> <b>NovaPSA</b> — the PSA on an independent financial system of record.</span>
        <button className="btn ghost sm" onClick={() => navigate('/login')}>Sign in →</button>
      </footer>
    </div>
  );
}
